import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import StatusBadge from '../components/StatusBadge';
import Money from '../components/Money';
import { fetchBuyerQuotationDetail, acceptBuyerQuotation, rejectBuyerQuotation } from '../api/marketplaceApi';

const QuotationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [quotation, setQuotation] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetchBuyerQuotationDetail(parseInt(id));
      setQuotation(res.quotation || res);
      setItems(res.items || res.quotation?.items || []);
    } catch (err) {
      console.error('Failed to load quotation:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleAccept = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      await acceptBuyerQuotation(parseInt(id));
      load();
    } catch (err: any) {
      alert(err.message || 'Failed to accept');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!id) return;
    const reason = window.prompt('Reason for rejection (optional):');
    setActionLoading(true);
    try {
      await rejectBuyerQuotation(parseInt(id), reason || undefined);
      load();
    } catch (err: any) {
      alert(err.message || 'Failed to reject');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <ArrowPathIcon className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (!quotation) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">Quotation not found</p>
        <button onClick={() => navigate('/marketplace/buyer/quotations')} className="mt-3 text-sm text-emerald-600 hover:underline">
          Back to Quotations
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate('/marketplace/buyer/quotations')}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeftIcon className="w-4 h-4" /> Back to Quotations
      </button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-900">Quotation {quotation.quotation_number}</h2>
            <StatusBadge status={quotation.status} />
          </div>
          <p className="text-sm text-gray-500 mt-1">
            From: {quotation.supplier_name || `Supplier #${quotation.supplier_tenant_id}`}
          </p>
        </div>
        {quotation.status === 'submitted' && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleReject}
              disabled={actionLoading}
              className="px-4 py-2 bg-white border border-red-200 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 disabled:opacity-50"
            >
              Reject
            </button>
            <button
              onClick={handleAccept}
              disabled={actionLoading}
              className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              {actionLoading ? 'Processing...' : 'Accept Quotation'}
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Items */}
          <div className="bg-white border border-gray-200 rounded-xl">
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700">Quotation Items</h3>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium text-gray-600">Item</th>
                  <th className="px-4 py-2 font-medium text-gray-600">Quantity</th>
                  <th className="px-4 py-2 font-medium text-gray-600">Unit Price</th>
                  <th className="px-4 py-2 font-medium text-gray-600 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {items.map((item: any) => (
                  <tr key={item.id}>
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-gray-900">{item.title}</p>
                      {item.grade && <p className="text-xs text-gray-400">Grade: {item.grade}</p>}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {item.commercial_quantity} {item.commercial_unit}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      <Money amount={item.unit_price} currency={item.currency} />
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium">
                      <Money amount={item.total_price} currency={item.currency} />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t border-gray-200">
                <tr>
                  <td colSpan={3} className="px-4 py-2.5 text-sm font-semibold text-gray-900">Subtotal</td>
                  <td className="px-4 py-2.5 text-right font-bold"><Money amount={quotation.subtotal} currency={quotation.currency} /></td>
                </tr>
                {quotation.delivery_fee > 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-2.5 text-sm text-gray-500">Delivery Fee</td>
                    <td className="px-4 py-2.5 text-right text-gray-600"><Money amount={quotation.delivery_fee} currency={quotation.currency} /></td>
                  </tr>
                )}
                {quotation.tax_amount > 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-2.5 text-sm text-gray-500">Tax</td>
                    <td className="px-4 py-2.5 text-right text-gray-600"><Money amount={quotation.tax_amount} currency={quotation.currency} /></td>
                  </tr>
                )}
                <tr className="bg-gray-50">
                  <td colSpan={3} className="px-4 py-2.5 text-sm font-bold text-gray-900">Total</td>
                  <td className="px-4 py-2.5 text-right font-bold text-lg"><Money amount={quotation.total_amount} currency={quotation.currency} /></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Notes */}
          {(quotation.payment_terms || quotation.quality_terms || quotation.notes) && (
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Terms & Notes</h3>
              <div className="space-y-3 text-sm">
                {quotation.payment_terms && (
                  <div>
                    <p className="font-medium text-gray-600">Payment Terms</p>
                    <p className="text-gray-500">{quotation.payment_terms}</p>
                  </div>
                )}
                {quotation.quality_terms && (
                  <div>
                    <p className="font-medium text-gray-600">Quality Terms</p>
                    <p className="text-gray-500">{quotation.quality_terms}</p>
                  </div>
                )}
                {quotation.notes && (
                  <div>
                    <p className="font-medium text-gray-600">Notes</p>
                    <p className="text-gray-500">{quotation.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                <StatusBadge status={quotation.status} size="sm" />
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">RFQ</span>
                <button onClick={() => navigate(`/marketplace/buyer/rfqs/${quotation.rfq_id}`)} className="text-emerald-600 hover:underline text-xs">
                  View RFQ
                </button>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Lead Time</span>
                <span>{quotation.delivery_lead_days ? `${quotation.delivery_lead_days} days` : '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Valid For</span>
                <span>{quotation.validity_days ? `${quotation.validity_days} days` : '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Revision</span>
                <span>#{quotation.revision_number || 1}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Created</span>
                <span>{new Date(quotation.created_at).toLocaleDateString()}</span>
              </div>
              {quotation.submitted_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Submitted</span>
                  <span>{new Date(quotation.submitted_at).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          {quotation.status === 'submitted' && (
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Actions</h3>
              <div className="space-y-2">
                <button
                  onClick={handleAccept}
                  disabled={actionLoading}
                  className="w-full px-4 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                >
                  Accept & Convert to Order
                </button>
                <button
                  onClick={handleReject}
                  disabled={actionLoading}
                  className="w-full px-4 py-2.5 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Reject Quotation
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuotationDetail;
